/**
 * Generic reinforcement-ratio visualisation. Plots a horizontal bar with a
 * RED zone below `rho_min` (deficient), a GREEN admissible band between
 * `rho_min` and `rho_max`, an optional darker-green "recommended" band up to
 * `rho_rec`, and a RED zone above `rho_max`. A cursor marks the current
 * `rho` value, glowing in the tone of its zone.
 *
 *   ── used in columns (1%–2.5%–6%), losas, muros, zapatas ──
 *
 * The component normalises everything to a single `bar_max` scale so the
 * bar visually represents the dimensionless ratio. Pass an `override_max`
 * when the natural maximum is unusually large (e.g. ρ_max = 8 % for some
 * sections); otherwise it picks a sensible default.
 */

interface RhoBarProps {
  /** Current reinforcement ratio (dimensionless, e.g. 0.0125 = 1.25 %). */
  rho: number;
  /** Code minimum. Below this the design is deficient. */
  rho_min: number;
  /** Code maximum. Above this the design is over-reinforced. */
  rho_max: number;
  /** Optional "recommended max" (slightly darker green band). */
  rho_rec?: number;
  /** Optional label override for the top-center caption (default "ρ"). */
  label?: string;
  /** Override the bar's right-edge value (default = ρ_max · 1.4). */
  bar_max?: number;
  /** Short note/title displayed above the bar. */
  title?: string;
  /** Hide the helper text at the bottom. */
  hideAdvice?: boolean;
}

export function RhoBar({
  rho,
  rho_min,
  rho_max,
  rho_rec,
  label = 'ρ',
  bar_max,
  title,
  hideAdvice = false,
}: RhoBarProps) {
  const max = bar_max ?? Math.max(rho_max * 1.4, rho * 1.2, rho_min * 5);
  const safeMax = max > 0 ? max : 1;
  const pct = Math.max(0, Math.min(rho / safeMax, 1)) * 100;

  const inRange = rho >= rho_min - 1e-9 && rho <= rho_max + 1e-9;
  const aboveRec = rho_rec !== undefined && rho > rho_rec;
  const tone = !inRange ? 'var(--danger)' : aboveRec ? 'var(--warning)' : 'var(--success)';

  // Helper to format ρ as a percentage with 2-3 significant decimals
  const fmtPct = (r: number): string => {
    const p = r * 100;
    if (p >= 1) return `${p.toFixed(2)}%`;
    return `${p.toFixed(3)}%`;
  };

  return (
    <div className="flex flex-col gap-1.5">
      {title && (
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
      )}

      <div className="flex items-center justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}>
        <span>0%</span>
        <span style={{ color: tone, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
          {label} = {fmtPct(rho)}
        </span>
        <span>{fmtPct(safeMax)}</span>
      </div>

      <div
        className="relative h-2.5 w-full overflow-hidden rounded-[var(--radius-pill)]"
        style={{ background: 'var(--surface-3, #1e293b)' }}
      >
        {/* Admissible band [ρ_min, ρ_max] */}
        <span
          className="absolute top-0 h-full"
          style={{
            left: `${(rho_min / safeMax) * 100}%`,
            width: `${Math.max(0, ((rho_max - rho_min) / safeMax) * 100)}%`,
            background: 'rgba(34,197,94,0.20)',
          }}
        />
        {/* Recommended band [ρ_min, ρ_rec] (darker green) */}
        {rho_rec !== undefined && rho_rec > rho_min && (
          <span
            className="absolute top-0 h-full"
            style={{
              left: `${(rho_min / safeMax) * 100}%`,
              width: `${Math.max(0, ((Math.min(rho_rec, rho_max) - rho_min) / safeMax) * 100)}%`,
              background: 'rgba(34,197,94,0.35)',
            }}
          />
        )}

        {/* Tick marks at ρ_min, ρ_rec, ρ_max */}
        {[
          { x: rho_min, color: 'var(--success)' },
          ...(rho_rec !== undefined ? [{ x: rho_rec, color: 'var(--warning)' }] : []),
          { x: rho_max, color: 'var(--danger)' },
        ].filter((m) => m.x <= safeMax).map((m, i) => (
          <span
            key={`tick-${i}`}
            className="absolute top-0 h-full w-px"
            style={{ left: `${(m.x / safeMax) * 100}%`, background: m.color }}
          />
        ))}

        {/* Cursor for current rho */}
        <span
          className="absolute -top-0.5 h-3.5 w-1 rounded-full transition-all duration-200"
          style={{
            left: `calc(${pct}% - 2px)`,
            background: tone,
            boxShadow: `0 0 0 2px var(--surface-1), 0 0 8px ${tone}`,
          }}
        />
      </div>

      <div className="flex items-center justify-between text-[9px] font-mono" style={{ color: 'var(--text-muted)' }}>
        <span style={{ color: 'var(--success)' }}>ρ_min {fmtPct(rho_min)}</span>
        {rho_rec !== undefined && (
          <span style={{ color: 'var(--warning)' }}>{fmtPct(rho_rec)} rec.</span>
        )}
        <span style={{ color: 'var(--danger)' }}>ρ_max {fmtPct(rho_max)}</span>
      </div>

      {!hideAdvice && (
        <div className="text-[10px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.4 }}>
          {!inRange && rho < rho_min && (
            <>
              <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Bajo el mínimo. </span>
              Aumentar el acero o reducir la sección de concreto.
            </>
          )}
          {!inRange && rho > rho_max && (
            <>
              <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Sobre el máximo. </span>
              Hay congestión de acero. Agrandar la sección o reducir el armado.
            </>
          )}
          {inRange && aboveRec && (
            <>
              <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Sobre el recomendado. </span>
              Cumple norma pero supera la cuantía óptima.
            </>
          )}
          {inRange && !aboveRec && (
            <>
              <span style={{ color: 'var(--success)', fontWeight: 600 }}>Dentro del rango. </span>
              Cuantía óptima.
            </>
          )}
        </div>
      )}
    </div>
  );
}
